import { getAllPhotos, getAllHunts, clearAllPhotos, clearAllHunts, upsertPhoto, upsertHunt } from './db';

/**
 * Export all local data (every hunt and every photo) to a downloadable JSON
 * file. Includes thumbnailDataUrl for offline portability.
 */
export async function exportData() {
  const [photos, hunts] = await Promise.all([getAllPhotos(), getAllHunts()]);

  const payload = {
    version: 2,
    exportedAt: new Date().toISOString(),
    hunts,
    photos,
  };

  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `photohunt-export-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Import data from a JSON string.
 * @param {string} jsonString
 * @param {'merge'|'replace'} mode
 * @param {function} dispatch  - React dispatch from AppContext
 * @param {string|null} activeHuntId - currently-selected hunt, so we can
 *   fall back to a valid hunt if a 'replace' import drops it
 */
export async function importData(jsonString, mode, dispatch, activeHuntId) {
  let payload;
  try {
    payload = JSON.parse(jsonString);
  } catch {
    throw new Error('Invalid JSON file');
  }

  if (!payload.photos || !Array.isArray(payload.photos)) {
    throw new Error('File does not contain a valid photo list');
  }
  if (!payload.hunts || !Array.isArray(payload.hunts)) {
    throw new Error('File does not contain a valid hunt list — exports from Number Hunter are not compatible with Photo Hunter');
  }

  if (mode === 'replace') {
    await clearAllPhotos();
    await clearAllHunts();
    for (const hunt of payload.hunts) await upsertHunt(hunt);
    for (const photo of payload.photos) await upsertPhoto(photo);
  } else {
    // Merge: keep existing, overwrite only if incoming record is newer
    const [existingHunts, existingPhotos] = await Promise.all([getAllHunts(), getAllPhotos()]);
    const existingHuntMap = new Map(existingHunts.map(h => [h.id, h]));
    const existingPhotoMap = new Map(existingPhotos.map(p => [p.id, p]));

    for (const hunt of payload.hunts) {
      const current = existingHuntMap.get(hunt.id);
      if (!current || (hunt.updatedAt ?? '') > (current.updatedAt ?? '')) {
        await upsertHunt(hunt);
      }
    }
    for (const photo of payload.photos) {
      const current = existingPhotoMap.get(photo.id);
      if (!current || photo.addedAt > current.addedAt) {
        await upsertPhoto(photo);
      }
    }
  }

  const [allHunts, allPhotos] = await Promise.all([getAllHunts(), getAllPhotos()]);
  dispatch({ type: 'SET_HUNTS', hunts: allHunts });
  dispatch({ type: 'SET_PHOTOS', photos: allPhotos });

  if (!allHunts.some(h => h.id === activeHuntId) && allHunts[0]) {
    dispatch({ type: 'SET_ACTIVE_HUNT', huntId: allHunts[0].id });
  }
}
